import React, { useEffect, useState, useMemo, useCallback } from 'react';
import * as yup from 'yup';

import { ReactComponent as ErrorResultIcon } from '../../../icons/error-result.svg';

import './form-modal.scss';

import { Button } from '../../common/button/Button';
import { Input } from '../../common/input/Input';
import { Modal } from '../../common/modal/Modal';
import { Select } from '../../common/select/Select';
import { TabItem, TabLayout } from '../../common/tab-layout/TabLayout';
import { Textarea } from '../../common/textarea/Textarea';
import { LinkedTextarea } from '../../common/textarea/linked/LinkedTextarea';
import { PageLoader } from '../../loader/PageLoader';
import { YES_NO_OPTIONS } from '../../page/clients/client.schema';

export const FormModal = ({
  isOpen,
  onClose,
  title,
  tabs,
  isEdit = false,
  itemId,
  onSuccess,
  onError,
  formData = {},
  onFormChange,
  onFieldChange,
  saveItem,
  updateItem,
  display = false,
  loading = false,
  error = false
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createMore, setCreateMore] = useState(false);

  const handleClearForm = useCallback((formData) => {
    setCurrentFormData(formData || {});
    setErrors({});
    setIsSubmitting(false);
    setCreateMore(false);
    setActiveTab(0);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      handleClearForm(formData);
    }
  }, [handleClearForm, isOpen, formData]);

  const [currentFormData, setCurrentFormData] = useState(formData);

  useEffect(() => {
    setCurrentFormData(formData);
  }, [formData]);

  const getNormalizedValue = useCallback((e) => {
    if (typeof e !== 'object' || e === null) {
      return e;
    }
    if (e?.target?.value !== undefined) {
      return e.target.value;
    }
    if (e?.value !== undefined) {
      return e.value;
    }
    return e;
  }, []);

  const handleInternalFieldChange = useCallback(
    (fieldName, value) => {
      let updatedData = { ...currentFormData, [fieldName]: value };

      tabs.forEach((tab) => {
        tab.fields.forEach((field) => {
          if (field.linkedKey === fieldName) {
            const isLinkedKey = `${field.name}Linked`;
            if (currentFormData[isLinkedKey]) {
              updatedData[field.name] = value;
            }
          }
        });
      });

      setCurrentFormData(updatedData);

      onFormChange?.(updatedData);
      onFieldChange?.({ name: fieldName }, value);
    },
    [currentFormData, onFormChange, onFieldChange, tabs]
  );

  const handleLinkedTextareaChange = useCallback(
    (fieldName, { value, isLinked }) => {
      const isLinkedKey = `${fieldName}Linked`;
      const updatedData = {
        ...currentFormData,
        [fieldName]: value,
        [isLinkedKey]: isLinked
      };

      setCurrentFormData(updatedData);

      onFormChange?.(updatedData);
      onFieldChange?.({ name: fieldName }, value);
    },
    [currentFormData, onFormChange, onFieldChange]
  );

  const validationSchema = useMemo(() => {
    const fields = tabs.flatMap((tab) => tab.fields);
    const shape = fields.reduce((acc, field) => {
      if (field.validation) acc[field.name] = field.validation;
      return acc;
    }, {});
    return yup.object().shape(shape);
  }, [tabs]);

  const validateField = async (fieldName, value) => {
    try {
      await yup.reach(validationSchema, fieldName).validate(value);
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fieldName]: err.message }));
    }
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(currentFormData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = err.inner.reduce(
        (acc, currentError) => ({ ...acc, [currentError.path]: currentError.message }),
        {}
      );
      setErrors(newErrors);
      return false;
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    const isValid = await validateForm();

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      const dataToSubmit = { ...currentFormData };

      if (isEdit && itemId && updateItem) {
        await updateItem(itemId, dataToSubmit);
      } else if (saveItem) {
        await saveItem(dataToSubmit);
      }
      if (!createMore) {
        onSuccess?.(false);
      } else {
        handleClearForm({});
        onSuccess?.(true);
      }
    } catch (err) {
      onError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    if (field.editOnly && !isEdit) return null;

    const commonProps = {
      value: currentFormData[field.name] || '',
      label: field.label,
      required: field.required,
      display: display || field.display,
      error: errors[field.name]
    };

    switch (field.type) {
      case 'input':
        return (
          <Input
            key={field.name}
            {...commonProps}
            bordered
            onChange={(e) => {
              const value = getNormalizedValue(e);
              handleInternalFieldChange(field.name, value);
              void validateField(field.name, value);
            }}
          />
        );
      case 'hidden':
        return <input key={field.name} type="hidden" value={currentFormData[field.name] || ''} />;
      case 'select':
        return (
          <Select
            key={field.name}
            {...commonProps}
            showLabel
            options={field.options}
            multiSelect={field.multiSelect}
            onChange={(val) => {
              const value = getNormalizedValue(val);
              handleInternalFieldChange(field.name, value);
              void validateField(field.name, value);
            }}
          />
        );
      case 'textarea':
        if (field.linkedKey) {
          const isLinkedKey = `${field.name}Linked`;
          const isLinked = currentFormData[isLinkedKey];
          return (
            <LinkedTextarea
              key={field.name}
              {...commonProps}
              bordered
              linkedValue={currentFormData[field.linkedKey] || ''}
              linkedLabel={field.linkedLabel}
              isLinked={isLinked}
              onChange={({ value, isLinked: newIsLinkedState }) => {
                handleLinkedTextareaChange(field.name, { value, isLinked: newIsLinkedState });
                void validateField(field.name, value);
              }}
            />
          );
        } else {
          return (
            <Textarea
              key={field.name}
              name={field.name}
              {...commonProps}
              bordered
              onChange={(e) => {
                const value = getNormalizedValue(e);
                handleInternalFieldChange(field.name, value);
                void validateField(field.name, value);
              }}
            />
          );
        }
      case 'date':
        return (
          <Input
            key={field.name}
            {...commonProps}
            type="date"
            bordered
            onChange={(e) => {
              const value = getNormalizedValue(e);
              handleInternalFieldChange(field.name, value);
              void validateField(field.name, value);
            }}
          />
        );
      case 'yesno':
        return (
          <Select
            key={field.name}
            {...commonProps}
            value={currentFormData[field.name] ? 'true' : 'false'}
            showLabel
            options={YES_NO_OPTIONS}
            onChange={(val) => {
              const stringVal = getNormalizedValue(val);
              const boolVal = stringVal === 'true';
              handleInternalFieldChange(field.name, boolVal);
              void validateField(field.name, boolVal);
            }}
          />
        );
      case 'empty':
        return <div key={field.name} />;
      default:
        return null;
    }
  };

  // Check for errors per tab.
  const tabErrors = useMemo(() => {
    return tabs.map((tab) =>
      tab.fields.some((field) => {
        const value = currentFormData[field.name];
        const isRequired = field.validation?.describe?.()?.optional === false;

        return (
          isRequired &&
          (value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === ''))
        );
      })
    );
  }, [tabs, currentFormData]);

  return (
    <Modal title={title} isOpen={isOpen} onClose={onClose} className="form-modal">
      {error && (
        <div className="form-modal__error-result">
          <ErrorResultIcon />
          <span>There was an error fetching your data.</span>
        </div>
      )}
      {!error && (
        <PageLoader loading={loading}>
          <form onSubmit={onSubmit}>
            <TabLayout activeIndex={activeTab} onChange={setActiveTab} renderAllTabs>
              {tabs.map((tab, idx) => (
                <TabItem
                  key={idx}
                  title={
                    <>
                      {tab.label}
                      {/* The asterisk now uses the per-tab error state */}
                      {tabErrors[idx] && !display && <span className="tab-required"> *</span>}
                    </>
                  }
                >
                  <div className="form-modal__tab-fields">
                    {tab.fields.map((field) => {
                      if (field.editOnly && !isEdit) {
                        return null;
                      }

                      return (
                        <div
                          key={field.name}
                          className={`form-modal__field ${field.colSpan === 2 ? 'form-modal__field--full' : ''} ${field.className || ''}`}
                        >
                          {renderField(field)}
                        </div>
                      );
                    })}
                  </div>
                </TabItem>
              ))}
            </TabLayout>
            <div className="form-modal__footer">
              <div>
                {!isEdit && (
                  <label className="form-modal__create-more">
                    <input
                      id="create-more"
                      name="create-more"
                      type="checkbox"
                      checked={createMore}
                      onChange={() => setCreateMore(!createMore)}
                      disabled={display}
                    />
                    Create another
                  </label>
                )}
              </div>
              <div>
                <Button secondary={display} tertiary={!display} type="button" onClick={onClose}>
                  {display ? 'Ok' : 'Cancel'}
                </Button>
                {!display && (
                  <Button secondary type="submit" loading={isSubmitting}>
                    {isEdit ? 'Save' : 'Create'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </PageLoader>
      )}
    </Modal>
  );
};
